/**
 * One small drawing per technique, in the same line language as the icons.
 *
 * Most of them are a right-and-wrong pair, because that is how these things are
 * actually understood: nobody learns "pieces should be even" from a picture of
 * even pieces, they learn it from seeing even next to ragged. The two halves are
 * labelled in words as well as marked, so the meaning doesn't depend on colour.
 *
 * Deliberately geometric. A cartoon fridge with a face is exactly the register
 * this app has spent five drops staying out of.
 */

import type { TechniqueId } from "../features/techniques/types";

import "./TechniqueDiagram.css";

interface TechniqueDiagramProps {
  id: TechniqueId;
}

/** A right/wrong pair sharing one viewBox, with a divider between them. */
function Pair({ good, bad }: { good: React.ReactNode; bad: React.ReactNode }) {
  return (
    <>
      <g className="diagram__good">{good}</g>
      <line className="diagram__divider" x1="100" y1="12" x2="100" y2="88" />
      <g className="diagram__bad">{bad}</g>
    </>
  );
}

const DRAWINGS: Record<TechniqueId, React.ReactNode> = {
  // Uniform squares against a jumble of sizes.
  "even-dice": (
    <Pair
      good={
        <>
          {[0, 1, 2].map((row) =>
            [0, 1, 2].map((col) => (
              <rect key={`${row}-${col}`} x={26 + col * 22} y={26 + row * 22} width="14" height="14" rx="2" />
            ))
          )}
        </>
      }
      bad={
        <>
          <rect x="122" y="22" width="24" height="19" rx="2" />
          <rect x="152" y="28" width="9" height="9" rx="2" />
          <rect x="124" y="48" width="12" height="16" rx="2" />
          <rect x="142" y="45" width="27" height="12" rx="2" />
          <rect x="126" y="70" width="8" height="8" rx="2" />
          <rect x="140" y="64" width="17" height="17" rx="2" />
        </>
      }
    />
  ),

  // A crust line under the food against nothing at all.
  "hot-pan-sear": (
    <Pair
      good={
        <>
          <path d="M20 62h60" />
          <rect x="30" y="38" width="40" height="20" rx="4" />
          <path className="diagram__mark" d="M30 58h40" strokeWidth="4" />
          <path d="M40 28c0-4 4-4 4-8M52 28c0-4 4-4 4-8" />
        </>
      }
      bad={
        <>
          <path d="M120 62h60" />
          <rect x="130" y="38" width="40" height="20" rx="4" />
          <path d="M133 66c4 3 8 3 12 0M153 66c4 3 8 3 12 0" />
        </>
      }
    />
  ),

  // Spaced on a tray against piled up.
  "dont-crowd": (
    <Pair
      good={
        <>
          <rect x="16" y="22" width="70" height="56" rx="5" />
          {[0, 1].map((row) =>
            [0, 1].map((col) => (
              <circle key={`${row}-${col}`} cx={36 + col * 30} cy={40 + row * 22} r="9" />
            ))
          )}
        </>
      }
      bad={
        <>
          <rect x="116" y="22" width="70" height="56" rx="5" />
          {[0, 1, 2].map((row) =>
            [0, 1, 2].map((col) => (
              <circle key={`${row}-${col}`} cx={131 + col * 20} cy={36 + row * 18} r="10" />
            ))
          )}
        </>
      }
    />
  ),

  // A spoon channel that holds against one that closes.
  reduce: (
    <Pair
      good={
        <>
          <path d="M18 40a32 32 0 0 0 64 0" />
          <path d="M18 40h64" />
          <path className="diagram__mark" d="M34 52h32" strokeWidth="3" />
          <path d="M34 52v10M66 52v10" />
        </>
      }
      bad={
        <>
          <path d="M118 40a32 32 0 0 0 64 0" />
          <path d="M118 40h64" />
          <path d="M134 56h32" strokeDasharray="3 5" />
        </>
      }
    />
  ),

  // One merged body against two separated layers.
  emulsify: (
    <Pair
      good={
        <>
          <path d="M20 40a30 30 0 0 0 60 0" />
          <path d="M20 40h60" />
          <path className="diagram__mark" d="M26 52a24 24 0 0 0 48 0" strokeWidth="3" />
        </>
      }
      bad={
        <>
          <path d="M120 40a30 30 0 0 0 60 0" />
          <path d="M120 40h60" />
          <path d="M126 56a24 24 0 0 0 48 0" />
          <path d="M128 50h44" strokeDasharray="4 4" />
        </>
      }
    />
  ),

  // Curled fingertips against flat ones, with the blade in the same place.
  "knife-grip": (
    <Pair
      good={
        <>
          <path d="M18 70h64" />
          <path d="M40 20v40" strokeWidth="3" />
          <path d="M40 60h22" strokeWidth="3" />
          <path d="M62 62c0-8 6-12 6-20" />
          <path d="M52 60c0-7 5-9 5-15" />
          <path d="M44 60c0-6 4-8 4-13" />
        </>
      }
      bad={
        <>
          <path d="M118 70h64" />
          <path d="M140 20v40" strokeWidth="3" />
          <path d="M140 60h22" strokeWidth="3" />
          <path className="diagram__warn" d="M144 60h34" />
          <path className="diagram__warn" d="M150 66h28" />
        </>
      }
    />
  ),

  // Three pinches over time against one at the end.
  "season-in-layers": (
    <Pair
      good={
        <>
          <path d="M16 74h70" />
          {[26, 50, 74].map((x) => (
            <g key={x}>
              <path d={`M${x} 34v14`} />
              <circle cx={x} cy={30} r="4" />
              <circle cx={x} cy={56} r="1.6" className="diagram__dot" />
              <circle cx={x - 5} cy={62} r="1.6" className="diagram__dot" />
              <circle cx={x + 5} cy={63} r="1.6" className="diagram__dot" />
            </g>
          ))}
        </>
      }
      bad={
        <>
          <path d="M116 74h70" />
          <path d="M176 34v14" />
          <circle cx="176" cy="30" r="4" />
          {[52, 58, 64, 68, 60, 55].map((y, i) => (
            <circle key={i} cx={172 + (i % 3) * 5} cy={y} r="1.6" className="diagram__dot" />
          ))}
        </>
      }
    />
  ),

  // Juice staying in against a puddle on the board.
  "rest-meat": (
    <Pair
      good={
        <>
          <path d="M18 68h64" />
          <rect x="32" y="40" width="38" height="22" rx="6" />
          <path className="diagram__mark" d="M42 50h18" strokeWidth="3" />
          <path d="M46 26v8M56 26v8" strokeDasharray="3 3" />
        </>
      }
      bad={
        <>
          <path d="M118 68h64" />
          <rect x="132" y="40" width="38" height="22" rx="6" />
          <path className="diagram__warn" d="M124 68c8-8 26-8 34 0" />
        </>
      }
    />
  ),
};

export function TechniqueDiagram({ id }: TechniqueDiagramProps) {
  return (
    <figure className="diagram">
      <svg className="diagram__art" viewBox="0 0 200 100" role="img" aria-hidden="true">
        {DRAWINGS[id]}
      </svg>
      {/* Words, not just position — the drawing alone is a Rorschach test. */}
      <figcaption className="diagram__captions">
        <span className="diagram__caption" data-side="good">
          This
        </span>
        <span className="diagram__caption" data-side="bad">
          Not this
        </span>
      </figcaption>
    </figure>
  );
}
